<template>
  <Dialog as="div" :open="localIsModalOpen" @close="handleCloseModal">
    <div class="fixed inset-0 z-10 bg-black/20" />
    <div class="fixed inset-0 z-20 flex items-center justify-center p-4">
      <DialogPanel
        class="flex w-full max-w-3xl flex-col gap-4 rounded-lg bg-white p-6 shadow-lg"
      >
        <DialogTitle class="capitalize-first text-2xl font-bold">
          {{ upperFirst($t('global.deposit_manage')) }}
        </DialogTitle>

        <div class="space-y-4">
          <!-- Montant standard -->
          <div class="capitalize-first ml-2 text-lg font-medium">
            {{
              $t('global.standard_deposit') +
              ' : ' +
              $h.formatCurrency(standardDepositAmount, invoice.currency)
            }}
          </div>

          <!-- Inputs & options -->
          <div class="grid grid-cols-7 items-start gap-4">
            <!-- Deposit input -->
            <div class="col-span-3">
              <BaseShowEditMeasureInput
                v-model="tempDeposit"
                :label="''"
                name="deposit"
                :measure="invoice.currency === 'CHF' ? 'CHF' : '€'"
                :disabled="invoice.deposit_type === 'auto'"
                :edition-mode
              />
              <p
                v-if="
                  parseFloat(tempDeposit) > parseFloat(invoice.total_tvac)
                "
                class="capitalize-first mt-1 text-xs text-red-600"
              >
                {{ $t('global.deposit_exceeds_invoice_alert') }}
              </p>
            </div>

            <!-- Deposit type -->
            <WalkmanButton
              :key="invoice.deposit_type"
              v-model="invoice.deposit_type"
              class="col-span-2"
              :options="[
                { value: 'fix', label: $t('global.fix') },
                { value: 'auto', label: $t('global.automatic') }
              ]"
            />
            <!--              :edition-mode="invoice.status === 'DRA'"-->
          </div>
          <div class="col-span-2 flex justify-start">
            <BaseButton
              :title="$t('global.apply_standard_deposit')"
              color="warning"
              class="px-3 py-1"
              @click="tempDeposit = standardDepositAmount"
            />
          </div>
          <div v-if="depositPayments?.length > 0" class="mt-6">
            <div class="capitalize-first mb-2 ml-2 font-medium">
              {{ $t('global.payment_deposit_list') }}
            </div>
            <div
              class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            >
              <div
                v-for="deposit in depositPayments"
                :key="deposit.id"
                class="bg-primary-100 flex flex-col gap-2 rounded-lg border p-3 select-none"
              >
                <div class="flex items-center justify-between">
                  <div class="truncate text-lg font-bold">
                    {{ $h.formatCurrency(deposit.amount, invoice.currency) }}
                  </div>
                  <div class="flex gap-1">
                    <p
                      v-if="deposit.payment_status === 'AWA'"
                      class="inline-flex items-center rounded-full bg-orange-200 px-2 py-0.5 text-xs font-semibold text-black"
                    >
                      {{ $t('attributes.snoozed') }}
                    </p>
                    <p
                      v-if="deposit.payment_status === 'REC'"
                      class="bg-theme-success inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold text-black"
                    >
                      {{ $t('enums.invoicing.paymentStatus.REC') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer buttons -->
        <div class="mt-6 flex justify-end gap-3">
          <BaseButton
            :title="$t('button.cancel')"
            color="warning"
            @click="handleCloseModal"
          />
          <BaseButton
            :title="$t('button.save')"
            :disabled="
              parseFloat(tempDeposit) > parseFloat(invoice.total_tvac)
            "
            @click="updateInvoice"
          />
          <BaseButton
            v-if="
              invoice.id &&
              parseFloat(tempDeposit) > 0 &&
              sumDepositPayments < parseFloat(tempDeposit)
            "
            :title="$t('button.save_and_add_payment')"
            :disabled="
              parseFloat(tempDeposit) > parseFloat(invoice.total_tvac) ||
              tempDeposit == 0
            "
            @click="updateInvoiceAndAddPayment"
          />
        </div>
      </DialogPanel>
    </div>

    <PaymentInvoiceFormModal
      v-model:is-modal-open="isPaymentFormModalOpen"
      :invoice="invoice"
      :payment-data="paymentData"
      @close="handleCloseModal"
    />
  </Dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { upperFirst } from 'lodash-es'
import BaseShowEditMeasureInput from '@/views/settings/invoicing/item/form/tabs/information/component/components/BaseShowEditMeasureInput.vue'
import PaymentInvoiceFormModal from '@ui/modal/payment/PaymentInvoiceFormModal.vue'
import WalkmanButton from '@c/WalkmanButton.vue'

export default {
  name: 'DepositModal',
  components: {
    WalkmanButton,
    PaymentInvoiceFormModal,
    BaseShowEditMeasureInput,
    BaseButton,
    Dialog,
    DialogTitle,
    DialogPanel
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close', 'update:is-modal-open'],
  data() {
    return {
      loading: false,
      tempDeposit: 0,
      isPaymentFormModalOpen: false,
      paymentData: {},
      standardDepositAmount: 0,
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice',
      metadata: 'invoicing/getInvoiceMetadata'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    depositPayments() {
      return this.invoice.payments.filter(
        (payment) => payment.is_payment_deposit
      )
    },
    sumDepositPayments() {
      return this.depositPayments.reduce((accumulator, payment) => {
        return accumulator + parseFloat(payment.amount)
      }, 0)
    },
    arrondi() {
      return this.metadata?.deposit_settings?.arrondi
    }
  },
  watch: {
    'invoice.deposit_type'(newValue, oldValue) {
      if (newValue !== oldValue && newValue === 'auto') {
        this.tempDeposit = this.standardDepositAmount
      }
    },
    // 'invoice.deposit'(newValue) {
    //   if (!newValue && newValue !== 0) return
    //
    //   if (
    //     this.arrondi != null &&
    //     this.arrondi !== 0 &&
    //     this.arrondi !== 'none'
    //   ) {
    //     const arrondiInt = parseInt(this.arrondi, 10)
    //     const depositNumber = parseFloat(newValue)
    //
    //     if (!isNaN(depositNumber)) {
    //       const rounded = Number(depositNumber.toFixed(arrondiInt))
    //
    //       if (rounded !== depositNumber) {
    //         this.$nextTick(() => {
    //           this.invoice.deposit = rounded
    //         })
    //       }
    //     }
    //   }
    // }
  },
  // mounted() {
  //   this.calculDefaultDeposit()
  // },
  beforeMount() {
    this.getStandardDepositAmount()
  },
  methods: {
    ...mapActions({
      processUpdateInvoiceAction: 'invoicing/updateInvoice',
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID',
      getStandardDepositAmountAction: 'invoicing/getStandardDepositAmount'
    }),
    upperFirst,
    updateInvoice() {
      this.invoice.deposit = this.tempDeposit
      this.processUpdateInvoiceAction({
        id: this.invoice.id,
        invoice: this.invoice
      }).then((_response) => {
        this.$h.toastSuccess(this.$t('toasts.save_deposit'))
        this.localIsModalOpen = false
        // this.$emit('update:is-modal-open', value)
      })
    },
    async getStandardDepositAmount() {
      this.getStandardDepositAmountAction({
        id: this.invoice.id,
      }).then((_response) => {
        this.standardDepositAmount = parseFloat(_response.standardDepositAmount)
        this.tempDeposit = this.invoice.deposit
      })
    },
    async updateInvoiceAndAddPayment() {
      this.invoice.deposit = this.tempDeposit
      await this.processUpdateInvoiceAction({
        id: this.invoice.id,
        invoice: this.invoice
      }).then((_response) => {
        this.$h.toastSuccess(this.$t('toasts.save_deposit'))
        const amount = this.invoice.deposit - this.sumDepositPayments
        this.paymentData = {
          amount: amount,
          payment_status: 'AWA',
          is_payment_deposit: true
        }
        this.isPaymentFormModalOpen = true
      })
    },
    async handleCloseModal() {
      this.$emit('close')
      this.localIsModalOpen = false
    }
  }
}
</script>
